import type { DomainEvent } from "./events";
import type { YJMockData } from "./seed";
import type { BrandId, BrokerId, CustomerId, MaaiStage, Order, OrderId, Settlement } from "./types";

const DAY = 86_400_000;
const PLATFORM_SHARE = 0.45;
const BROKER_COMMISSION_RATE = 0.05;
const PAYMENT_GATEWAY_RATE = 0.025;

function isRevenueEvent(event: DomainEvent) {
  return (
    event.type === "order.maai1.created" ||
    event.type === "order.maai2.closed" ||
    event.type === "order.maai3plus.detected"
  );
}

function orderAmountByEvent(state: YJMockData, event: DomainEvent) {
  if (event.type === "order.maai1.created" || event.type === "order.maai2.closed") return event.amount;
  if (event.type === "order.maai3plus.detected") {
    return state.orders.find((order) => order.id === event.orderId)?.amount ?? 0;
  }
  return 0;
}

function eventMaai(event: DomainEvent): MaaiStage | null {
  if (event.type === "order.maai1.created") return 1;
  if (event.type === "order.maai2.closed") return 2;
  if (event.type === "order.maai3plus.detected") return event.maai;
  return null;
}

export function selectLedgerGMV(state: YJMockData, days?: number) {
  const cutoff = days ? state.clock.now - days * DAY : Number.NEGATIVE_INFINITY;
  return state.events
    .filter((event) => isRevenueEvent(event) && event.at >= cutoff)
    .reduce((sum, event) => sum + orderAmountByEvent(state, event), 0);
}

export function selectByMaai(state: YJMockData) {
  const totals: Record<MaaiStage, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
  state.events.forEach((event) => {
    const maai = eventMaai(event);
    if (!maai) return;
    totals[maai] += orderAmountByEvent(state, event);
  });
  return totals;
}

export function selectLedgerOrderIds(state: YJMockData) {
  return state.events
    .filter(isRevenueEvent)
    .map((event) => ("orderId" in event ? event.orderId : null))
    .filter((id): id is OrderId => id !== null);
}

export function selectRevenueOrders(state: YJMockData) {
  const ledgerOrderIds = new Set(selectLedgerOrderIds(state));
  return state.orders.filter((order) => ledgerOrderIds.has(order.id) && order.status !== "rejected" && order.status !== "cancelled");
}

export function selectSettlementSnapshot(state: YJMockData, settlementId = "s1"): Settlement {
  const base = state.settlements.find((settlement) => settlement.id === settlementId) ?? state.settlements[0];
  const revenueOrders = selectRevenueOrders(state);
  const gmv = selectLedgerGMV(state);
  const gatewayFee = revenueOrders
    .filter((order) => order.paymentMethod === "QR" || order.paymentMethod === "CARD")
    .reduce((sum, order) => sum + order.amount * PAYMENT_GATEWAY_RATE, 0);
  const marketingSpent = state.campaigns.reduce((sum, campaign) => sum + campaign.spent, 0);
  const fixedCost = base?.fixedCost ?? 86_000;
  const periodStart = base?.periodStart ?? state.clock.now - 30 * DAY;
  const periodEnd = base?.periodEnd ?? state.clock.now;
  const paidOut = base?.paidOut ?? false;
  return {
    id: base?.id ?? settlementId,
    periodStart,
    periodEnd,
    gmv,
    fixedCost,
    gatewayFee,
    marketingSpent,
    netProfit: gmv * PLATFORM_SHARE - fixedCost - gatewayFee - marketingSpent,
    paidOut,
    paidAt: base?.paidAt,
  };
}

export function selectA1Metrics(state: YJMockData) {
  const settlement = selectSettlementSnapshot(state);
  const byMaai = selectByMaai(state);
  const houseBrand = selectHouseBrandShare(state);
  return {
    gmvDay: selectLedgerGMV(state, 1),
    gmvMonth: selectLedgerGMV(state, 30),
    gmvYear: selectLedgerGMV(state, 365),
    byMaai,
    settlement,
    houseBrand,
    activeBrokers: selectActiveBrokers(state),
    activeSellers: selectActiveSellers(state),
  };
}

export function selectActiveBrokers(state: YJMockData) {
  return state.brokers.filter((broker) => selectBrokerEarn(state, broker.id) > 0 || broker.totalEarn > 0).length;
}

export function selectActiveSellers(state: YJMockData) {
  return state.brands.filter((brand) => brand.status === "active" && !brand.isHouse).length;
}

export function selectHouseBrandShare(state: YJMockData) {
  const revenueOrders = selectRevenueOrders(state);
  const total = revenueOrders.reduce((sum, order) => sum + order.amount, 0);
  const house = revenueOrders
    .filter((order) => state.brands.find((brand) => brand.id === order.brandId)?.isHouse)
    .reduce((sum, order) => sum + order.amount, 0);
  return { total, house, general: total - house };
}

export function selectBrokerEarn(state: YJMockData, brokerId: BrokerId) {
  return state.orders
    .filter((order) => order.brokerId === brokerId && order.status !== "rejected" && order.status !== "cancelled")
    .reduce((sum, order) => sum + order.amount * BROKER_COMMISSION_RATE, 0);
}

export function selectTopBrokers(state: YJMockData, limit = 3) {
  return state.brokers
    .map((broker) => ({ broker, earn: selectBrokerEarn(state, broker.id) }))
    .filter((row) => row.earn > 0)
    .sort((left, right) => right.earn - left.earn)
    .slice(0, limit);
}

export function selectCustomerHistory(state: YJMockData, customerId: CustomerId) {
  return state.orders
    .filter((order) => order.customerId === customerId)
    .sort((left, right) => right.createdAt - left.createdAt);
}

export function selectPendingLeads(state: YJMockData) {
  return state.leads.filter((lead) => lead.status === "pending" || lead.status === "calling");
}

export function selectSupplierPayout(state: YJMockData, brandId: BrandId) {
  const brand = state.brands.find((item) => item.id === brandId);
  if (!brand) return null;
  const brandOrders = state.orders.filter((order) => order.brandId === brandId);
  const gross = brandOrders.reduce((sum, order) => sum + order.amount, 0);
  const supplierShare = gross * (1 - brand.gp / 100);
  const existing = state.supplierPayouts.find((payout) => payout.brandId === brandId);
  return {
    brandId,
    gross,
    supplierShare,
    withdrawn: existing?.withdrawn ?? 0,
    available: Math.max(0, supplierShare - (existing?.withdrawn ?? 0)),
    history: existing?.history ?? [],
  };
}

export function selectBrokerCustomers(state: YJMockData, brokerId: BrokerId) {
  const orders = state.orders.filter((order) => order.brokerId === brokerId);
  return Array.from(new Set(orders.map((order) => order.customerId))).map((customerId) => {
    const customer = state.customers.find((item) => item.id === customerId);
    const customerOrders = orders.filter((order) => order.customerId === customerId);
    return {
      customer,
      orders: customerOrders,
      maxMaai: Math.max(...customerOrders.map((order) => order.maai)),
    };
  });
}

export function selectOrderById(state: YJMockData, orderId: OrderId): Order | undefined {
  return state.orders.find((order) => order.id === orderId);
}
